import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["enqueued", "scheduled", "retry", "failed", "queueList", "jobCounts",
                    "duplicatesInfo", "clearJobClass", "uniqueLocks", "statusMessage"]

  connect() {
    this.refreshStats()
  }

  async refreshStats() {
    try {
      const response = await fetch('/admin/control_panel/queue_stats')
      const data = await response.json()

      this.enqueuedTarget.textContent = data.enqueued
      this.scheduledTarget.textContent = data.scheduled_size
      this.retryTarget.textContent = data.retry_size
      this.failedTarget.textContent = data.failed

      this.renderQueueList(data.queues)
    } catch (error) {
      this.showStatus('Failed to load queue stats: ' + error.message, 'error')
    }
  }

  renderQueueList(queues) {
    if (!queues || queues.length === 0) {
      this.queueListTarget.innerHTML = '<p class="text-gray-500 text-sm">No queues found.</p>'
      return
    }

    const html = queues.map(q => `
      <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
        <span class="font-medium">${q.name}</span>
        <div class="flex items-center gap-4">
          <span class="text-sm text-gray-600">Size: <strong>${q.size}</strong></span>
          <span class="text-sm text-gray-600">Latency: ${q.latency}s</span>
        </div>
      </div>
    `).join('')

    this.queueListTarget.innerHTML = html
  }

  async loadJobCounts() {
    try {
      const response = await fetch('/admin/control_panel/job_counts_by_class')
      const data = await response.json()

      if (Object.keys(data).length === 0) {
        this.jobCountsTarget.innerHTML = '<p class="text-gray-500 text-sm">No jobs in queues.</p>'
        return
      }

      const html = Object.entries(data).map(([klass, count]) => `
        <div class="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
          <span class="font-mono text-sm">${klass}</span>
          <div class="flex items-center gap-2">
            <span class="px-2 py-1 text-xs font-medium ${count > 1 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'} rounded">${count}</span>
            <button type="button"
                    data-action="click->queue-management#selectJobClass"
                    data-job-class="${klass.split(' ')[0]}"
                    class="text-xs text-blue-600 hover:text-blue-800">Select</button>
          </div>
        </div>
      `).join('')

      this.jobCountsTarget.innerHTML = html
    } catch (error) {
      this.showStatus('Failed to load job counts: ' + error.message, 'error')
    }
  }

  selectJobClass(event) {
    const jobClass = event.currentTarget.dataset.jobClass
    this.clearJobClassTarget.value = jobClass
  }

  async findDuplicates() {
    try {
      this.duplicatesInfoTarget.innerHTML = '<p class="text-yellow-700 text-sm">Scanning for duplicates...</p>'
      const response = await fetch('/admin/control_panel/find_duplicate_jobs')
      const data = await response.json()

      if (Object.keys(data).length === 0) {
        this.duplicatesInfoTarget.innerHTML = '<p class="text-green-600 text-sm">No duplicate jobs found.</p>'
        return
      }

      let totalDuplicates = 0
      const html = Object.entries(data).map(([klass, queues]) => {
        const queueInfo = queues.map(q => {
          totalDuplicates += q.count - 1
          return `${q.queue}: ${q.count} jobs`
        }).join(', ')
        return `<div class="p-2 bg-yellow-100 rounded mb-1"><strong>${klass}</strong>: ${queueInfo}</div>`
      }).join('')

      this.duplicatesInfoTarget.innerHTML = `
        <p class="text-yellow-800 text-sm mb-2">Found ${totalDuplicates} duplicate job(s):</p>
        ${html}
      `
    } catch (error) {
      this.showStatus('Failed to find duplicates: ' + error.message, 'error')
    }
  }

  async cleanDuplicates() {
    if (!confirm('Are you sure you want to clean all duplicate jobs? This will keep only the oldest job per class/arguments combination.')) {
      return
    }

    try {
      this.showStatus('Starting duplicate cleanup job...', 'info')

      const response = await fetch('/admin/control_panel/clean_duplicate_jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      const data = await response.json()

      if (data.success && data.job_id) {
        this.showStatus('Cleanup job started. Checking progress...', 'info')
        this.pollCleanupStatus(data.job_id)
      } else {
        this.showStatus('Failed to start cleanup: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      this.showStatus('Failed to start cleanup: ' + error.message, 'error')
    }
  }

  async pollCleanupStatus(jobId) {
    const maxAttempts = 120 // 10 minutes max (5 second intervals)
    let attempts = 0

    const checkStatus = async () => {
      attempts++

      try {
        const response = await fetch(`/admin/control_panel/clean_duplicate_jobs_status?job_id=${jobId}`)
        const data = await response.json()

        if (data.status === 'complete') {
          const removed = data.result?.removed_count || 0
          this.showStatus(`Cleanup complete: removed ${removed} duplicate job(s)`, 'success')
          this.refreshStats()
          this.findDuplicates()
          return
        } else if (data.status === 'failed') {
          this.showStatus('Cleanup job failed', 'error')
          return
        } else if (attempts >= maxAttempts) {
          this.showStatus('Cleanup is taking longer than expected. Check Sidekiq for status.', 'warning')
          return
        }

        // Still running - show progress and continue polling
        const progress = data.progress || 0
        const message = data.message || 'Processing...'
        this.showStatus(`Cleanup in progress: ${progress}% - ${message}`, 'info')

        setTimeout(checkStatus, 5000) // Check every 5 seconds
      } catch (error) {
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000)
        } else {
          this.showStatus('Failed to check cleanup status: ' + error.message, 'error')
        }
      }
    }

    checkStatus()
  }

  async clearJobsByClass() {
    const jobClass = this.clearJobClassTarget.value.trim()
    if (!jobClass) {
      this.showStatus('Please enter a job class name', 'error')
      return
    }

    if (!confirm(`Are you sure you want to remove all "${jobClass}" jobs from all queues?`)) {
      return
    }

    try {
      const response = await fetch('/admin/control_panel/clear_jobs_by_class', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ job_class: jobClass })
      })
      const data = await response.json()

      if (data.success) {
        this.showStatus(data.message, 'success')
        this.clearJobClassTarget.value = ''
        this.refreshStats()
      } else {
        this.showStatus('Failed to clear jobs: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      this.showStatus('Failed to clear jobs: ' + error.message, 'error')
    }
  }

  async listUniqueLocks() {
    try {
      this.uniqueLocksTarget.innerHTML = '<p class="text-gray-500 text-sm">Loading locks...</p>'
      const response = await fetch('/admin/control_panel/list_unique_locks')
      const data = await response.json()

      if (data.count === 0) {
        this.uniqueLocksTarget.innerHTML = '<p class="text-green-600 text-sm">No unique locks found.</p>'
        return
      }

      const html = `
        <p class="text-gray-600 text-sm mb-2">Found ${data.count} unique lock(s):</p>
        <div class="max-h-40 overflow-y-auto space-y-1">
          ${data.locks.map(lock => `
            <div class="p-2 bg-gray-100 rounded text-xs font-mono">
              <div class="truncate">${lock.key}</div>
              <div class="text-gray-500">TTL: ${lock.ttl}s</div>
            </div>
          `).join('')}
        </div>
      `

      this.uniqueLocksTarget.innerHTML = html
    } catch (error) {
      this.showStatus('Failed to list locks: ' + error.message, 'error')
    }
  }

  async clearUniqueLocks() {
    if (!confirm('Are you sure you want to clear all unique job locks? This may allow previously blocked duplicate jobs to run.')) {
      return
    }

    try {
      const response = await fetch('/admin/control_panel/clear_unique_locks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      const data = await response.json()

      if (data.success) {
        this.showStatus(data.message, 'success')
        this.listUniqueLocks()
      } else {
        this.showStatus('Failed to clear locks: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      this.showStatus('Failed to clear locks: ' + error.message, 'error')
    }
  }

  showStatus(message, type) {
    const element = this.statusMessageTarget
    element.textContent = message

    const colorClasses = {
      success: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800',
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800'
    }

    element.className = `mt-4 p-4 rounded-lg ${colorClasses[type] || colorClasses.info}`
    element.classList.remove('hidden')

    // Only auto-hide for success/error, keep info/warning visible during polling
    if (type === 'success' || type === 'error') {
      setTimeout(() => {
        element.classList.add('hidden')
      }, 5000)
    }
  }
}
